import { createRoute } from '@tanstack/react-router'
import { adminRoute } from '../route'
import { useEffect, useState } from 'react'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Bot, CheckCircle2, AlertCircle, FileCog, ToggleRight } from 'lucide-react'

export const adminToolsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tools',
  component: AdminToolsPage,
})

function AdminToolsPage() {
  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-foreground mb-6">Tools</h1>
      <div className="space-y-6">
        <AppSettingsTool />
        <ResetChatAgentTool />
        <TriggerDocumentPipelineTool />
      </div>
    </div>
  )
}

/**
 * Pull projectId + threadId out of a chat URL or path. Matches our route
 * shape `/<projectId>/chat/<chatId>` (with optional origin, query, hash).
 * Returns null when the input doesn't contain a `/<id>/chat/<id>` segment.
 */
function parseChatUrl(input: string): { projectId: string; threadId: string } | null {
  const match = input.trim().match(/\/?([^/\s?#]+)\/chat\/([^/\s?#]+)/)
  if (!match) return null
  return { projectId: match[1], threadId: match[2] }
}

/**
 * Pull projectId + documentId out of a document URL or path. Matches our
 * route shape `/<projectId>/write/<docId>`.
 */
function parseDocumentUrl(input: string): { projectId: string; documentId: string } | null {
  const match = input.trim().match(/\/?([^/\s?#]+)\/write\/([^/\s?#]+)/)
  if (!match) return null
  return { projectId: match[1], documentId: match[2] }
}

type AppSettings = Awaited<ReturnType<typeof orpc.admin.getAppSettings>>['settings']

/** Global switches (every organization and project) — `app_settings` in D1, defaults on. */
function AppSettingsTool() {
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    orpc.admin
      .getAppSettings()
      .then((res) => setSettings(res.settings))
      .catch(() => setError('Could not load the settings.'))
  }, [])

  const toggle = async (key: keyof AppSettings, value: boolean) => {
    setSaving(true)
    setError(null)
    try {
      const res = await orpc.admin.updateAppSettings({ [key]: value })
      setSettings(res.settings)
    } catch {
      setError('Could not save the setting. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const rows: Array<{ key: keyof AppSettings; title: string; description: string }> = [
    {
      key: 'jam',
      title: 'Jam bug reports',
      description:
        'Loads the Jam (jam.dev) recorder in the app for every organization so users can report bugs with a screen recording, console and network logs. Reports carry app version, route and project role — never names or e-mail. Takes effect on the next page load.',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ToggleRight className="h-5 w-5" />
          Global settings
        </CardTitle>
        <CardDescription>Apply to every organization and project. On by default.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map((row) => (
          <div key={row.key} className="flex items-start justify-between gap-6">
            <div>
              <p className="text-sm font-medium">{row.title}</p>
              <p className="text-sm text-muted-foreground">{row.description}</p>
            </div>
            <Switch
              checked={settings?.[row.key] ?? false}
              disabled={settings === null || saving}
              aria-label={row.title}
              onCheckedChange={(next) => void toggle(row.key, next)}
            />
          </div>
        ))}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}

function ResetChatAgentTool() {
  const [projectId, setProjectId] = useState('')
  const [threadId, setThreadId] = useState('')
  const [drainQueue, setDrainQueue] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ reset: boolean; queueDrained: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * If the user pastes a full chat URL into either ID field, split it across
   * both inputs. Otherwise treat the value as a plain ID.
   */
  const handleIdInput = (target: 'project' | 'thread') => (value: string) => {
    const parsed = parseChatUrl(value)
    if (parsed) {
      setProjectId(parsed.projectId)
      setThreadId(parsed.threadId)
      return
    }
    if (target === 'project') setProjectId(value)
    else setThreadId(value)
  }

  const canSubmit = projectId.trim().length > 0 && threadId.trim().length > 0

  const handleSubmit = () => {
    setResult(null)
    setError(null)
    setShowConfirm(true)
  }

  const handleConfirm = async () => {
    setShowConfirm(false)
    setLoading(true)
    setResult(null)
    setError(null)

    try {
      const res = await orpc.admin.resetChatAgent({
        projectId: projectId.trim(),
        threadId: threadId.trim(),
        drainQueue,
      })
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-foreground" />
            <CardTitle>Reset chat agent</CardTitle>
          </div>
          <CardDescription>
            Resets an AI agent that is stuck in a tool call or thinking state. The agent status is
            set to idle and the change is pushed immediately to all connected clients.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Tip: paste the chat URL (e.g.{' '}
            <code className="bg-muted px-1 py-0.5 rounded">.../proj_xxx/chat/thrd_yyy</code>) into
            either field — the project and thread IDs are filled in automatically.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="projectId">Project ID</Label>
              <Input
                id="projectId"
                placeholder="proj_... or chat URL"
                value={projectId}
                onChange={(e) => handleIdInput('project')(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="threadId">Thread ID</Label>
              <Input
                id="threadId"
                placeholder="thrd_... or chat URL"
                value={threadId}
                onChange={(e) => handleIdInput('thread')(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="drainQueue"
              checked={drainQueue}
              onCheckedChange={(checked) => setDrainQueue(checked === true)}
            />
            <Label htmlFor="drainQueue" className="text-sm font-normal cursor-pointer">
              Drain message queue
            </Label>
          </div>

          <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? 'Resetting...' : 'Reset agent'}
          </Button>

          {result && (
            <div className="flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-200">
                  Agent has been reset
                </p>
                {result.queueDrained > 0 && (
                  <p className="text-green-700 dark:text-green-300 mt-0.5">
                    {result.queueDrained} queued message{result.queueDrained !== 1 ? 's' : ''}{' '}
                    drained
                  </p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200">Error</p>
                <p className="text-red-700 dark:text-red-300 mt-0.5">{error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm reset</DialogTitle>
            <DialogDescription>Are you sure you want to reset the agent?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm py-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project</span>
              <code className="bg-muted px-2 py-0.5 rounded text-xs">{projectId.trim()}</code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Thread</span>
              <code className="bg-muted px-2 py-0.5 rounded text-xs">{threadId.trim()}</code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Drain queue</span>
              <span>{drainQueue ? 'Yes' : 'No'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function TriggerDocumentPipelineTool() {
  const [projectId, setProjectId] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ triggered: boolean; documentTitle: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleIdInput = (target: 'project' | 'document') => (value: string) => {
    const parsed = parseDocumentUrl(value)
    if (parsed) {
      setProjectId(parsed.projectId)
      setDocumentId(parsed.documentId)
      return
    }
    if (target === 'project') setProjectId(value)
    else setDocumentId(value)
  }

  const canSubmit = projectId.trim().length > 0 && documentId.trim().length > 0

  const handleSubmit = () => {
    setResult(null)
    setError(null)
    setShowConfirm(true)
  }

  const handleConfirm = async () => {
    setShowConfirm(false)
    setLoading(true)
    setResult(null)
    setError(null)

    try {
      const res = await orpc.admin.triggerDocumentPipeline({
        projectId: projectId.trim(),
        documentId: documentId.trim(),
      })
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileCog className="h-5 w-5 text-foreground" />
            <CardTitle>Re-run document pipeline</CardTitle>
          </div>
          <CardDescription>
            Queues the conversion and indexing pipeline for an existing document. Use when a
            document is stuck or needs to be re-indexed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Tip: paste the document URL (e.g.{' '}
            <code className="bg-muted px-1 py-0.5 rounded">.../proj_xxx/write/doc_yyy</code>) into
            either field — the project and document IDs are filled in automatically.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="doc-projectId">Project ID</Label>
              <Input
                id="doc-projectId"
                placeholder="proj_... or document URL"
                value={projectId}
                onChange={(e) => handleIdInput('project')(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc-documentId">Document ID</Label>
              <Input
                id="doc-documentId"
                placeholder="doc_... or document URL"
                value={documentId}
                onChange={(e) => handleIdInput('document')(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? 'Queuing...' : 'Run pipeline'}
          </Button>

          {result && (
            <div className="flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-200">
                  Pipeline queued for {result.documentTitle}
                </p>
                <p className="text-green-700 dark:text-green-300 mt-0.5">
                  Status was set to <code>pending</code> and the conversion job was dispatched.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200">Error</p>
                <p className="text-red-700 dark:text-red-300 mt-0.5">{error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm pipeline run</DialogTitle>
            <DialogDescription>
              Are you sure you want to re-queue the full conversion and indexing pipeline for this
              document?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm py-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project</span>
              <code className="bg-muted px-2 py-0.5 rounded text-xs">{projectId.trim()}</code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Document</span>
              <code className="bg-muted px-2 py-0.5 rounded text-xs">{documentId.trim()}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
